import MapComponent from 'components/common/MapComponent';
import FeatherIcon from 'feather-icons-react';
import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from 'services/authService';
import {
  ProjectImage,
  VideoProject,
  videoProjectService
} from 'services/videoService';

const VideoTriage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [projectId, setProjectId] = useState<string | null>(null);
  const [projects, setProjects] = useState<VideoProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<VideoProject | null>(
    null
  );
  const [loadingProjects, setLoadingProjects] = useState<boolean>(false);

  // Image viewer state
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [images, setImages] = useState<ProjectImage[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);

  // Video player state
  const [videoLoading, setVideoLoading] = useState<boolean>(true);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [videoCanPlay, setVideoCanPlay] = useState<boolean>(false);
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [videoMetadata, setVideoMetadata] = useState<{
    projectId?: string;
    projectName?: string;
    videoFilename?: string;
    directAccess?: boolean;
    authenticationRequired?: boolean;
  }>({});

  // Visited locations for map markers
  const [visitedLocations, setVisitedLocations] = useState<
    Array<{
      id: string;
      lat: number;
      lng: number;
      title: string;
    }>
  >([]);
  const [mapKey, setMapKey] = useState(0);

  // Fetch video URL from API endpoint
  const fetchVideoUrl = async (projectId: string) => {
    try {
      console.log(`Fetching video URL for project: ${projectId}`);
      setVideoLoading(true);
      setVideoError(null);

      const response = await videoProjectService.getVideoUrl(projectId);
      console.log('Video URL API response:', response);

      if (response?.video_url) {
        console.log('Setting video URL:', response.video_url);
        setVideoUrl(response.video_url);
        setVideoMetadata({
          projectId: response.project_id,
          projectName: response.project_name,
          videoFilename: response.video_filename,
          directAccess: response.direct_access,
          authenticationRequired: response.authentication_required
        });
        console.log('Video URL set successfully, waiting for video to load...');
        // Don't set loading to false here - let video events handle it
        return response.video_url;
      } else {
        throw new Error('No video URL returned from API');
      }
    } catch (error) {
      console.error('Error fetching video URL:', error);
      setVideoError('Erro ao obter URL do vídeo da API');
      setVideoLoading(false);
      throw error;
    }
  };

  // Test API connectivity
  const testVideoEndpoint = async () => {
    if (!selectedProject?.id) {
      alert('❌ Nenhum projeto selecionado');
      return;
    }

    try {
      console.log(
        'Testing video URL endpoint for project:',
        selectedProject.id
      );

      // First test the video-url endpoint
      const response = await videoProjectService.getVideoUrl(
        String(selectedProject.id)
      );
      console.log('Video URL API response:', response);

      if (response?.video_url) {
        // Test the actual video URL
        console.log('Testing video file access:', response.video_url);
        const videoResponse = await fetch(response.video_url, {
          method: 'GET',
          headers: {
            Range: 'bytes=0-1023' // Only request first 1KB to test connectivity
          }
        });

        console.log('Video file response:', {
          status: videoResponse.status,
          statusText: videoResponse.statusText,
          headers: Object.fromEntries(videoResponse.headers.entries())
        });

        if (videoResponse.ok || videoResponse.status === 206) {
          alert(
            `✅ API endpoint and video accessible!\nProject: ${response.project_name}\nVideo: ${response.video_filename}`
          );
          // Abort the response to prevent downloading
          videoResponse.body?.cancel?.();
        } else {
          alert(
            `❌ Video file error: ${videoResponse.status} ${videoResponse.statusText}`
          );
        }
      } else {
        alert('❌ No video URL returned from API');
      }
    } catch (error) {
      console.error('API Test Error:', error);
      alert(`❌ Network Error: ${error}`);
    }
  };

  // Function to fetch images for a project using video service
  const fetchProjectImages = async (projectId: string) => {
    try {
      const response = await videoProjectService.getProjectImages(projectId);
      setImages(response);
    } catch (error) {
      console.error('Error fetching project images:', error);

      // Check if it's a 401 error and try to refresh token first
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response: { status: number } };
        if (axiosError.response?.status === 401) {
          console.log('401 error detected, attempting token refresh...');
          try {
            await authService.refreshToken();
            console.log(
              'Token refreshed successfully, retrying image fetch...'
            );
            // Retry the request after token refresh
            const retryResponse = await videoProjectService.getProjectImages(
              projectId
            );
            setImages(retryResponse);
            return;
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            // Only redirect to home if token refresh also fails
            alert(
              'Sessão expirada. Você será redirecionado para a página de login.'
            );
            navigate('/');
            return;
          }
        }
      }
      setImages([]);
    }
  };

  // Check authentication on component mount (basic token existence check)
  useEffect(() => {
    const token = localStorage.getItem('authToken');

    if (!token) {
      console.log('No auth token found, redirecting to home');
      navigate('/');
      return;
    }

    console.log('Auth token found, proceeding with component initialization');
  }, [navigate]);

  // Load projects on component mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoadingProjects(true);
        const projectsData = await videoProjectService.getAll();
        if (Array.isArray(projectsData)) {
          setProjects(projectsData);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);

        // Check if it's a 401 error and try to refresh token
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as { response: { status: number } };
          if (axiosError.response?.status === 401) {
            console.log(
              '401 error on projects fetch, attempting token refresh...'
            );
            try {
              await authService.refreshToken();
              console.log('Token refreshed, retrying projects fetch...');
              const retryData = await videoProjectService.getAll();
              if (Array.isArray(retryData)) {
                setProjects(retryData);
              }
            } catch (refreshError) {
              console.error(
                'Token refresh failed during projects fetch:',
                refreshError
              );
              alert('Sessão expirada. Redirecionando para login...');
              navigate('/');
            }
          }
        }
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchProjects();
  }, []);

  // Handle URL projectId parameter
  useEffect(() => {
    const id = searchParams.get('projectId');
    setProjectId(id);

    // If projectId is provided, find and select the corresponding project
    if (id && projects.length > 0) {
      const project = projects.find(p => String(p.id) === id);
      setSelectedProject(project || null);
    }
  }, [searchParams, projects]);

  // Fetch images and video URL when project is selected
  useEffect(() => {
    if (selectedProject?.id) {
      fetchProjectImages(String(selectedProject.id));
      fetchVideoUrl(String(selectedProject.id)).catch(error => {
        console.error('Failed to fetch video URL:', error);
      });
    } else {
      setImages([]);
      setVideoUrl('');
      setVideoMetadata({});
      setVideoLoading(false);
      setVideoError(null);
      setVideoCanPlay(false);
    }
    // Reset visited locations when project changes
    setVisitedLocations([]);
  }, [selectedProject]);

  // Add timeout for video loading
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (videoLoading && videoUrl) {
      console.log('Setting video loading timeout (30 seconds)');
      timeoutId = setTimeout(() => {
        console.warn('Video loading timeout reached');
        setVideoLoading(false);
        setVideoError('Timeout: O vídeo demorou muito para carregar');
      }, 30000); // 30 second timeout
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [videoLoading, videoUrl]); // Handle slideshow functionality
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isPlaying && images.length > 0) {
      interval = setInterval(() => {
        setCurrentImageIndex(prevIndex => {
          if (prevIndex < images.length - 1) {
            return prevIndex + 1;
          } else {
            setIsPlaying(false); // Stop at the end
            return prevIndex;
          }
        });
      }, 2000); // Change image every 2 seconds
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isPlaying, images.length]);

  const handleProjectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = event.target.value;
    if (selectedId) {
      const project = projects.find(p => String(p.id) === selectedId);
      setSelectedProject(project || null);
      setProjectId(selectedId);
      setCurrentImageIndex(0); // Reset to first image when changing projects
      console.log('Selected project:', project);
      // Update URL to reflect the selected project
      navigate(`/app/video/triage?projectId=${selectedId}`);
    } else {
      setSelectedProject(null);
      setProjectId(null);
      setCurrentImageIndex(0);
      // Remove projectId from URL
      navigate('/app/video/triage');
    }
  };

  // Helper function to add current location to visited locations
  const addCurrentLocationToVisited = () => {
    console.log('addCurrentLocationToVisited called');
    console.log('images.length:', images.length);
    console.log('currentImageIndex:', currentImageIndex);

    if (images.length > 0 && images[currentImageIndex]) {
      const currentImage = images[currentImageIndex];
      console.log('Current image:', currentImage);
      console.log('Current image coords:', {
        lat: currentImage.lat,
        lng: currentImage.long
      });

      const locationId = `${currentImage.id}_${currentImage.filename}`;

      // Check if this location is already visited
      const isAlreadyVisited = visitedLocations.some(
        loc => loc.id === locationId
      );

      if (!isAlreadyVisited) {
        // Validate coordinates
        if (
          typeof currentImage.lat !== 'number' ||
          typeof currentImage.long !== 'number' ||
          isNaN(currentImage.lat) ||
          isNaN(currentImage.long)
        ) {
          console.error('Invalid coordinates:', {
            lat: currentImage.lat,
            lng: currentImage.long
          });
          return;
        }

        const newLocation = {
          id: locationId,
          lat: currentImage.lat,
          lng: currentImage.long,
          title: `Visitado: ${currentImage.filename}`
        };

        console.log('Adding new location:', newLocation);
        setVisitedLocations(prev => {
          const updated = [...prev, newLocation];
          console.log('Updated visitedLocations:', updated);
          return updated;
        });
      } else {
        console.log('Location already visited:', locationId);
      }
    } else {
      console.log('No current image available');
    }
  };

  const handlePreviousImage = () => {
    if (currentImageIndex > 0) {
      // Add current location to visited before moving
      addCurrentLocationToVisited();
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  const handleNextImage = () => {
    if (currentImageIndex < images.length - 1) {
      // Add current location to visited before moving
      addCurrentLocationToVisited();
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  return (
    <Container fluid>
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-1">
                Triagem de Vídeos
                {selectedProject && (
                  <small className="text-muted ms-2">
                    - {selectedProject.name}
                  </small>
                )}
              </h2>
              <p className="text-700 mb-0">
                {selectedProject
                  ? `Gerencie vídeos do projeto: ${selectedProject.name}`
                  : `Selecione um projeto para gerenciar seus vídeos`}
              </p>
            </div>
          </div>
        </Col>
      </Row>

      {/* Project Selection */}
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Select
              value={projectId || ''}
              onChange={handleProjectChange}
              disabled={loadingProjects}
            >
              <option value="">
                {loadingProjects
                  ? 'Carregando projetos...'
                  : 'Selecione um projeto'}
              </option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          {selectedProject && (
            <div className="mt-2">
              <small className="text-muted d-block">Projeto Selecionado:</small>
              <div className="fw-semibold">{selectedProject.name}</div>
              <div className="text-muted small">
                {selectedProject.description}
              </div>
            </div>
          )}
        </Col>
      </Row>

      {/* Debug Info */}
      {process.env.NODE_ENV === 'development' && (
        <Row className="mb-2">
          <Col>
            <div className="text-muted small">
              Debug: selectedProject = {selectedProject ? 'YES' : 'NO'},
              projects.length = {projects.length}, visitedLocations ={' '}
              {visitedLocations.length}
            </div>
          </Col>
        </Row>
      )}

      {/* 2x2 Grid Layout */}
      {selectedProject && (
        <>
          <Row className="mb-3">
            {/* Row 1, Column 1 - Video Player */}
            <Col md={6}>
              <Card className="h-100">
                <Card.Header>
                  <h6 className="mb-0">
                    <FeatherIcon
                      icon="play-circle"
                      size={16}
                      className="me-2"
                    />
                    Video Player
                  </h6>
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="position-relative">
                    <video
                      key={videoUrl} // Force remount when URL changes
                      controls
                      muted
                      playsInline
                      style={{
                        width: '100%',
                        height: '300px',
                        objectFit: 'contain',
                        backgroundColor: '#000'
                      }}
                      preload="auto"
                      crossOrigin="anonymous"
                      onLoadStart={e => {
                        console.log('Video: Load start - URL:', videoUrl);
                        console.log('Video element:', e.target);
                        setVideoLoading(true);
                        setVideoError(null);
                      }}
                      onLoadedMetadata={e => {
                        const video = e.target as HTMLVideoElement;
                        console.log('Video: Metadata loaded');
                        console.log('Video duration:', video.duration);
                        console.log(
                          'Video dimensions:',
                          video.videoWidth,
                          'x',
                          video.videoHeight
                        );
                        setVideoLoading(false);
                      }}
                      onLoadedData={() => {
                        console.log('Video: Data loaded');
                        setVideoLoading(false);
                      }}
                      onCanPlay={() => {
                        console.log('Video: Can play');
                        setVideoCanPlay(true);
                        setVideoLoading(false);
                      }}
                      onCanPlayThrough={() => {
                        console.log('Video: Can play through');
                        setVideoCanPlay(true);
                      }}
                      onProgress={() => {
                        console.log('Video: Progress (downloading)');
                      }}
                      onSuspend={() => {
                        console.log('Video: Suspend (download suspended)');
                      }}
                      onWaiting={() => {
                        console.log('Video: Waiting for data');
                      }}
                      onError={e => {
                        console.error('Video error:', e);
                        const video = e.target as HTMLVideoElement;
                        const error = video?.error;
                        console.error('Video error details:', {
                          code: error?.code,
                          message: error?.message,
                          videoUrl: videoUrl,
                          timestamp: new Date().toISOString()
                        });

                        let errorMessage = 'Erro ao carregar o vídeo da API';
                        if (error?.code === MediaError.MEDIA_ERR_NETWORK) {
                          errorMessage =
                            'Erro de rede - verifique se a API está acessível';
                        } else if (
                          error?.code === MediaError.MEDIA_ERR_DECODE
                        ) {
                          errorMessage =
                            'Erro de decodificação - formato não suportado';
                        } else if (
                          error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED
                        ) {
                          errorMessage =
                            'Formato AVI não suportado pelo navegador';
                        } else if (
                          error?.code === MediaError.MEDIA_ERR_ABORTED
                        ) {
                          errorMessage = 'Carregamento cancelado';
                        }

                        setVideoError(errorMessage);
                        setVideoLoading(false);
                        setVideoCanPlay(false);
                      }}
                      onAbort={() => {
                        console.log('Video: Aborted');
                        setVideoLoading(false);
                      }}
                      onStalled={() => {
                        console.log('Video: Stalled');
                      }}
                    >
                      {videoUrl && <source src={videoUrl} type="video/mp4" />}
                      Seu navegador não suporta o elemento de vídeo ou não foi
                      possível carregar o vídeo da API.
                    </video>

                    {/* Video Loading Overlay */}
                    {videoLoading && (
                      <div
                        className="position-absolute top-50 start-50 translate-middle text-center"
                        style={{ zIndex: 2 }}
                      >
                        <div className="bg-dark bg-opacity-75 p-3 rounded text-light">
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">
                              Carregando...
                            </span>
                          </div>
                          <div className="mt-2">
                            <small>Carregando vídeo MP4...</small>
                          </div>
                          {process.env.NODE_ENV === 'development' && (
                            <div className="mt-3">
                              <small className="d-block mb-2">
                                URL:{' '}
                                {videoUrl ? 'Disponível' : 'Não encontrada'}
                              </small>
                              <Button
                                variant="outline-light"
                                size="sm"
                                onClick={() => {
                                  console.log('=== LOADING DEBUG ===');
                                  console.log('videoUrl:', videoUrl);
                                  console.log('videoLoading:', videoLoading);
                                  const videoEl =
                                    document.querySelector('video');
                                  if (videoEl) {
                                    console.log(
                                      'Video element found:',
                                      videoEl
                                    );
                                    console.log(
                                      'Video readyState:',
                                      videoEl.readyState
                                    );
                                    console.log(
                                      'Video networkState:',
                                      videoEl.networkState
                                    );
                                    console.log('Video error:', videoEl.error);
                                    console.log(
                                      'Video src:',
                                      videoEl.currentSrc || videoEl.src
                                    );
                                  }
                                  // Force stop loading
                                  setVideoLoading(false);
                                }}
                              >
                                Debug & Stop
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Video Error Overlay */}
                    {videoError && (
                      <div
                        className="position-absolute top-50 start-50 translate-middle text-center"
                        style={{ zIndex: 2 }}
                      >
                        <div className="alert alert-danger" role="alert">
                          <FeatherIcon
                            icon="alert-circle"
                            size={24}
                            className="mb-2"
                          />
                          <div>Erro no vídeo</div>
                          <small className="d-block mb-3">{videoError}</small>
                          <div className="d-flex gap-2 justify-content-center flex-wrap">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => {
                                setVideoError('');
                                if (selectedProject?.id) {
                                  fetchVideoUrl(String(selectedProject.id));
                                }
                              }}
                            >
                              Tentar Novamente
                            </Button>
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={testVideoEndpoint}
                            >
                              Testar API
                            </Button>
                            <Button
                              variant="outline-warning"
                              size="sm"
                              onClick={() => {
                                console.log('=== VIDEO DEBUG INFO ===');
                                console.log('videoUrl:', videoUrl);
                                console.log('videoLoading:', videoLoading);
                                console.log('videoError:', videoError);
                                console.log('videoCanPlay:', videoCanPlay);
                                console.log('videoMetadata:', videoMetadata);
                                console.log(
                                  'selectedProject:',
                                  selectedProject
                                );
                                alert(
                                  `Debug Info (check console):\nURL: ${
                                    videoUrl || 'No URL'
                                  }\nLoading: ${videoLoading}\nError: ${
                                    videoError || 'None'
                                  }\nCan Play: ${videoCanPlay}`
                                );
                              }}
                            >
                              Debug
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Video Info Overlay */}
                    <div
                      className="position-absolute top-0 end-0 m-2"
                      style={{ zIndex: 1 }}
                    >
                      <small
                        className="badge bg-dark bg-opacity-75 text-light"
                        style={{ fontSize: '0.75rem' }}
                      >
                        {videoMetadata.videoFilename || 'Video não carregado'}
                        {videoCanPlay && (
                          <span className="ms-1 text-success">●</span>
                        )}
                      </small>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Row 1, Column 2 - Localização */}
            <Col md={6}>
              <Card className="h-100">
                <Card.Header>
                  <h6 className="m-0">
                    <FeatherIcon icon="map-pin" size={16} className="me-2" />
                    Localização
                    {images.length > 0 && images[currentImageIndex] && (
                      <small className="text-muted ms-2">
                        - {images[currentImageIndex].filename}
                      </small>
                    )}
                  </h6>
                </Card.Header>
                <Card.Body className="p-0">
                  <MapComponent
                    key={mapKey}
                    lat={
                      images.length > 0 && images[currentImageIndex]
                        ? images[currentImageIndex].lat ?? undefined
                        : undefined
                    }
                    lng={
                      images.length > 0 && images[currentImageIndex]
                        ? images[currentImageIndex].long ?? undefined
                        : undefined
                    }
                    title={
                      images.length > 0 && images[currentImageIndex]
                        ? `Localização Atual: ${images[currentImageIndex].filename}`
                        : 'Localização do projeto'
                    }
                    markers={visitedLocations}
                    height="300px"
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="mb-3">
            {/* Row 2, Column 1 - Informações da Imagem */}
            <Col md={6}>
              <Card className="h-100">
                <Card.Header>
                  <h6 className="mb-0">
                    <FeatherIcon icon="info" size={16} className="me-2" />
                    Informações da Imagem
                  </h6>
                </Card.Header>
                <Card.Body>
                  {images.length > 0 && images[currentImageIndex] ? (
                    <>
                      <div className="mb-3">
                        <small className="text-muted d-block">
                          Nome do Arquivo:
                        </small>
                        <div className="fw-semibold">
                          {images[currentImageIndex].filename}
                        </div>
                      </div>

                      <div className="mb-3">
                        <small className="text-muted d-block">Data/Hora:</small>
                        <div>
                          {videoProjectService.getFormattedTimestamp(
                            images[currentImageIndex].timestamp
                          )}
                        </div>
                      </div>

                      <div className="mb-3">
                        <small className="text-muted d-block">Tamanho:</small>
                        <div>
                          {videoProjectService.getFormattedFileSize(
                            images[currentImageIndex].size
                          )}
                        </div>
                      </div>

                      <div className="mb-3">
                        <small className="text-muted d-block">
                          Localização:
                        </small>
                        <div>
                          {(() => {
                            const currentImage = images[currentImageIndex];
                            return currentImage &&
                              currentImage.lat !== null &&
                              currentImage.long !== null ? (
                              <span>
                                {currentImage.lat.toFixed(6)},{' '}
                                {currentImage.long.toFixed(6)}
                              </span>
                            ) : (
                              'Não disponível'
                            );
                          })()}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mb-3">
                        <small className="text-muted d-block">
                          Nome do Arquivo:
                        </small>
                        <div className="fw-semibold text-muted">-</div>
                      </div>

                      <div className="mb-3">
                        <small className="text-muted d-block">Data/Hora:</small>
                        <div className="text-muted">-</div>
                      </div>

                      <div className="mb-3">
                        <small className="text-muted d-block">Tamanho:</small>
                        <div className="text-muted">-</div>
                      </div>

                      <div className="mb-3">
                        <small className="text-muted d-block">
                          Localização:
                        </small>
                        <div className="text-muted">-</div>
                      </div>
                    </>
                  )}

                  {/* Navigation Controls */}
                  <div className="d-flex justify-content-center align-items-center gap-3">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handlePreviousImage}
                      disabled={currentImageIndex === 0}
                      title="Imagem anterior"
                    >
                      <FeatherIcon icon="chevron-left" size={16} />
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setIsPlaying(!isPlaying)}
                      title={
                        isPlaying ? 'Pausar slideshow' : 'Iniciar slideshow'
                      }
                    >
                      <FeatherIcon
                        icon={isPlaying ? 'pause' : 'play'}
                        size={16}
                      />
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleNextImage}
                      disabled={currentImageIndex >= images.length - 1}
                      title="Próxima imagem"
                    >
                      <FeatherIcon icon="chevron-right" size={16} />
                    </Button>
                  </div>

                  <div className="text-center mt-3">
                    <small className="text-muted">
                      {images.length > 0
                        ? `Imagem ${currentImageIndex + 1} de ${images.length}`
                        : 'Nenhuma imagem carregada'}
                      {visitedLocations.length > 0 && (
                        <span className="ms-2">
                          • {visitedLocations.length} locais visitados
                        </span>
                      )}
                    </small>

                    {visitedLocations.length > 0 && (
                      <div className="mt-2">
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() => {
                            setVisitedLocations([]);
                            console.log('Marcadores limpos');
                          }}
                          title="Limpar marcadores visitados"
                          className="me-2"
                        >
                          <FeatherIcon
                            icon="trash-2"
                            size={14}
                            className="me-1"
                          />
                          Limpar Marcadores
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={addCurrentLocationToVisited}
                          title="Testar marcador na localização atual"
                          className="me-2"
                        >
                          <FeatherIcon icon="plus" size={14} className="me-1" />
                          Testar Marcador
                        </Button>
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => {
                            // Force map re-center by updating key
                            setMapKey(prev => prev + 1);
                          }}
                          title="Centralizar mapa na localização atual"
                        >
                          <FeatherIcon
                            icon="crosshair"
                            size={14}
                            className="me-1"
                          />
                          Centralizar
                        </Button>
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Row 2, Column 2 - Reserved Area */}
            <Col md={6}>
              <Card className="h-100">
                <Card.Header>
                  <h6 className="mb-0">
                    <FeatherIcon icon="square" size={16} className="me-2" />
                    Área Reservada
                  </h6>
                </Card.Header>
                <Card.Body>
                  <div
                    className="d-flex align-items-center justify-content-center bg-light text-muted"
                    style={{ height: '300px' }}
                  >
                    <div className="text-center">
                      <FeatherIcon icon="square" size={48} className="mb-2" />
                      <p className="mb-0">Área Reservada</p>
                      <small>
                        Esta área está disponível para futuras funcionalidades
                      </small>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default VideoTriage;
